import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { useCampus } from '../lib/useCampus';
import { api, ApiError, asList } from '../lib/api';
import { storeApi, shipmentsApi, type StoreItem, type Allocation, type StoreOrder, type Shipment } from '../lib/storeApi';

interface ClassLite { id: string; label: string; }
const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
function currentAY() { const n = new Date(); return n.getMonth() >= 5 ? `${n.getFullYear()}-${n.getFullYear() + 1}` : `${n.getFullYear() - 1}-${n.getFullYear()}`; }

export function StorePage() {
  const [tab, setTab] = useState<'catalogue' | 'measurements' | 'allocations' | 'orders' | 'shipments'>('catalogue');
  const [instId, setInstId] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassLite[]>([]);
  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);
  useEffect(() => { if (instId) api.get<ClassLite[]>(`/api/classes?institutionId=${instId}`).then((r) => setClasses(asList(r))).catch(() => setClasses([])); }, [instId]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>School store</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Store & allocations</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>Books, uniforms and supplies at your prices. Allocate to students, then charge — it lands in the parent's Payments.</p>
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <button className={tab === 'catalogue' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('catalogue')}>Catalogue</button>
        <button className={tab === 'measurements' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('measurements')}>Measurements</button>
        <button className={tab === 'allocations' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('allocations')}>Allocations</button>
        <button className={tab === 'orders' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('orders')}>Parent orders</button>
        <button className={tab === 'shipments' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('shipments')}>Deliveries</button>
      </div>
      {tab === 'catalogue' && <Catalogue instId={instId} classes={classes} />}
      {tab === 'measurements' && <Measurements classes={classes} />}
      {tab === 'allocations' && <Allocations instId={instId} classes={classes} items={[]} />}
      {tab === 'orders' && <ParentOrders />}
      {tab === 'shipments' && <Shipments />}
    </div>
  );
}

function Catalogue({ instId, classes }: { instId: string | null; classes: ClassLite[] }) {
  const [items, setItems] = useState<StoreItem[]>([]);
  const [name, setName] = useState(''); const [kind, setKind] = useState('Book'); const [price, setPrice] = useState(''); const [classId, setClassId] = useState('');
  const load = useCallback(() => { storeApi.items().then((r) => setItems(asList(r))).catch(() => setItems([])); }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim() || !instId) return;
    await storeApi.createItem({ institutionId: instId, name: name.trim(), kind, basePricePaise: Math.round(Number(price) * 100) || 0, academicClassId: classId || null });
    setName(''); setPrice(''); load();
  };
  const addVariant = async (itemId: string) => {
    const label = window.prompt('Variant label (e.g. "Size 24 · White")'); if (!label) return;
    const size = window.prompt('Size (e.g. 24) — used to match a student\'s size') || null;
    const colour = window.prompt('Colour (optional)') || null;
    const po = window.prompt('Price override in ₹ (blank = base price)');
    await storeApi.addVariant({ storeItemId: itemId, label, colour, size, priceOverridePaise: po ? Math.round(Number(po) * 100) : null });
    load();
  };
  const toggleOrderable = async (i: StoreItem) => { await storeApi.setOrderable(i.id, !i.parentOrderable); load(); };
  const restock = async (i: StoreItem) => { const by = Number(window.prompt(`Add stock for ${i.name}:`)); if (by > 0) { await storeApi.restock(i.id, by); load(); } };
  const setThreshold = async (i: StoreItem) => { const t = window.prompt(`Notify staff when ${i.name} stock falls to or below:`, String(i.lowStockThreshold)); if (t !== null) { await storeApi.setThreshold(i.id, Number(t) || 0); load(); } };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" placeholder="Item name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '2 1 180px' }} />
        <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 'auto' }}>
          <option value="Book">Book</option><option value="Uniform">Uniform</option><option value="Supply">Supply</option>
        </select>
        <input className="input" type="number" placeholder="Price ₹" value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: 100 }} />
        <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All classes</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button className="btn btn-accent" disabled={!instId} onClick={add}>Add item</button>
      </div>

      {items.map((i) => (
        <div key={i.id} className="card" style={{ padding: 12, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{i.name}</span>
              <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>{i.kind}</span>
              <span style={{ marginLeft: 8, color: 'var(--ink-2)' }}>{rupees(i.basePricePaise)}</span>
            </div>
            {i.kind === 'Uniform' && <button className="btn" onClick={() => addVariant(i.id)}>+ Variant</button>}
            <button className={i.parentOrderable ? 'btn btn-accent' : 'btn'} style={{ padding: '2px 10px' }} onClick={() => toggleOrderable(i)} title="Let parents order this from the portal">
              {i.parentOrderable ? <>Orderable · <span style={{ color: i.stock <= i.lowStockThreshold ? '#c1272d' : 'inherit', fontWeight: 700 }}>{i.stock}</span> stock</> : 'Make orderable'}
            </button>
            {i.parentOrderable && <button className="btn" style={{ padding: '2px 10px' }} onClick={() => restock(i)}>+ Stock</button>}
            {i.parentOrderable && <button className="btn" style={{ padding: '2px 10px' }} onClick={() => setThreshold(i)} title="Low-stock alert threshold">Alert ≤ {i.lowStockThreshold}</button>}
          </div>
          {i.variants.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
              {i.variants.map((v) => <span key={v.id} className="pill" style={{ marginRight: 6, fontSize: 11 }}>{v.label}{v.priceOverridePaise ? ` · ${rupees(v.priceOverridePaise)}` : ''}</span>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Measurements({ classes }: { classes: ClassLite[] }) {
  const [classId, setClassId] = useState('');
  const [year] = useState(currentAY());
  const [missing, setMissing] = useState<Array<{ studentId: string; studentName: string }>>([]);
  const [student, setStudent] = useState<{ id: string; fullName: string } | null>(null);
  const [search, setSearch] = useState(''); const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);
  const [h, setH] = useState(''); const [w, setW] = useState(''); const [size, setSize] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const loadMissing = useCallback(() => { if (classId) storeApi.missing(classId, year).then((r) => setMissing(asList(r))).catch(() => setMissing([])); }, [classId, year]);
  useEffect(loadMissing, [loadMissing]);

  const doSearch = async () => { if (search.trim().length < 2) return; try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=6`))); } catch { setResults([]); } };
  const save = async () => {
    if (!student) return;
    setMsg(null);
    try {
      await storeApi.recordMeasurement({ studentId: student.id, academicYear: year, heightCm: Number(h), widthCm: Number(w), sizeLabel: size.trim() || null });
      setMsg(`Saved for ${student.fullName}.`); setH(''); setW(''); setSize(''); setStudent(null); loadMissing();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not save.'); }
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Record measurement ({year})</div>
        {student ? <div style={{ marginBottom: 8 }}><strong>{student.fullName}</strong> <button className="btn" style={{ padding: '2px 8px' }} onClick={() => setStudent(null)}>Change</button></div>
          : <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="input" placeholder="Find student" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
              <button className="btn" onClick={doSearch}>Search</button>
            </div>}
        {!student && results.map((s) => <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }} onClick={() => { setStudent({ id: s.id, fullName: s.fullName }); setResults([]); }}>{s.fullName} · {s.admissionNumber}</button>)}
        {student && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><label className="label">Height (cm)</label><input className="input" type="number" value={h} onChange={(e) => setH(e.target.value)} style={{ width: 100 }} /></div>
            <div><label className="label">Width (cm)</label><input className="input" type="number" value={w} onChange={(e) => setW(e.target.value)} style={{ width: 100 }} /></div>
            <div><label className="label">Size label</label><input className="input" placeholder="24 / M" value={size} onChange={(e) => setSize(e.target.value)} style={{ width: 90 }} /></div>
            <button className="btn btn-accent" onClick={save}>Save</button>
          </div>
        )}
        {msg && <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 8 }}>{msg}</div>}
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <strong style={{ flex: 1 }}>Missing measurements</strong>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Pick a class…</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {!classId ? <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Pick a class to see who still needs measuring.</div>
          : missing.length === 0 ? <div style={{ color: '#1a7f37', fontSize: 13 }}>All students in this class have {year} measurements. ✓</div>
          : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {missing.map((m) => <span key={m.studentId} className="pill" style={{ fontSize: 12, background: '#fdecec', color: '#c1272d' }}>{m.studentName}</span>)}
            </div>}
      </div>
    </div>
  );
}

function Allocations({ instId, classes }: { instId: string | null; classes: ClassLite[]; items: StoreItem[] }) {
  const [classId, setClassId] = useState('');
  const [year] = useState(currentAY());
  const [uniforms, setUniforms] = useState<StoreItem[]>([]);
  const [uniformId, setUniformId] = useState('');
  const [allocs, setAllocs] = useState<Allocation[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { storeApi.items().then((its) => setUniforms(its.filter((i) => i.kind === 'Uniform'))).catch(() => setUniforms([])); }, []);
  const load = useCallback(() => { if (classId) storeApi.allocationsForClass(classId, year).then((r) => setAllocs(asList(r))).catch(() => setAllocs([])); }, [classId, year]);
  useEffect(load, [load]);

  const run = async () => {
    if (!classId || !instId) return;
    setMsg(null);
    try {
      const r = await storeApi.allocate({ academicClassId: classId, institutionId: instId, academicYear: year, uniformItemId: uniformId || null });
      setMsg(`Allocated ${r.allocated} students${r.skippedUniform.length ? `. No uniform sized for: ${r.skippedUniform.join(', ')} (measure them first).` : '.'}`);
      load();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Allocation failed.'); }
  };

  const act = async (fn: () => Promise<unknown>) => { try { await fn(); load(); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Action failed.'); } };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Class…</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select className="input" value={uniformId} onChange={(e) => setUniformId(e.target.value)} style={{ width: 'auto' }}>
          <option value="">No uniform</option>
          {uniforms.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button className="btn btn-accent" disabled={!classId || !instId} onClick={run}>Auto-allocate {year}</button>
      </div>
      {msg && <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13, color: 'var(--ink-2)' }}>{msg}</div>}

      {allocs.map((a) => (
        <div key={a.id} className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, flex: 1 }}>{a.studentName}</span>
            <span className="pill" style={{ fontSize: 11 }}>{a.status}</span>
            <span style={{ fontWeight: 700 }}>{rupees(a.totalPaise)}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 8 }}>
            {a.lines.map((l) => <div key={l.id}>{l.label} × {l.quantity} — {rupees(l.pricePaise * l.quantity)} {a.status === 'Draft' && <button className="btn" style={{ padding: '0 6px', marginLeft: 6 }} onClick={() => act(() => storeApi.removeLine(a.id, l.id))}>✕</button>}</div>)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {a.status === 'Draft' && <button className="btn" onClick={() => act(() => storeApi.confirm(a.id))}>Confirm</button>}
            {a.status === 'Confirmed' && <button className="btn btn-accent" onClick={() => act(() => storeApi.charge(a.id))}>Charge to parent</button>}
            {(a.status === 'Charged' || a.status === 'Confirmed') && <button className="btn" onClick={() => act(() => storeApi.handover(a.id))}>Mark handed over</button>}
            {a.invoiceId && <span style={{ fontSize: 12, color: '#1a7f37', alignSelf: 'center' }}>Charged ✓</span>}
          </div>
        </div>
      ))}
      {classId && allocs.length === 0 && <div style={{ color: 'var(--ink-3)', padding: 12 }}>No allocations yet — run auto-allocate.</div>}
    </div>
  );
}

function ParentOrders() {
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => { storeApi.openOrders().then((r) => setOrders(asList(r))).catch(() => setOrders([])); }, []);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => { setMsg(null); try { await fn(); load(); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Action failed.'); } };
  const cancel = async (id: string) => { const r = window.prompt('Reason for cancelling? (stock will be returned)'); if (r === null) return; act(() => storeApi.orderCancel(id, r || null)); };

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 12 }}>Orders parents placed from the portal. Mark ready when prepared, handed over when the student collects. Cancelling returns the stock.</p>
      {msg && <div className="card" style={{ padding: 10, marginBottom: 10, fontSize: 13, color: '#c1272d' }}>{msg}</div>}
      {orders.length === 0 ? <div style={{ color: 'var(--ink-3)', padding: 12 }}>No open orders.</div>
        : orders.map((o) => (
          <div key={o.id} className="card" style={{ padding: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{o.itemLabel} × {o.quantity}</span>
              <span style={{ color: 'var(--ink-2)' }}> — {o.studentName}</span>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{rupees(o.totalPaise)} · {o.payment === 'PaidOnline' ? 'pay now' : 'pay at pickup'}{o.isPaid ? ' · paid' : ''}</div>
            </div>
            <span className="pill" style={{ fontSize: 11 }}>{o.status}</span>
            {o.status === 'Placed' && <button className="btn" onClick={() => act(() => storeApi.orderReady(o.id))}>Ready</button>}
            {(o.status === 'Placed' || o.status === 'Ready') && <button className="btn btn-accent" onClick={() => act(() => storeApi.orderHandover(o.id))}>Handed over</button>}
            {(o.status === 'Placed' || o.status === 'Ready') && <button className="btn" onClick={() => cancel(o.id)}>Cancel</button>}
          </div>
        ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   DELIVERIES

   Counter collection is still the norm and always will be for most schools.
   This is for the parcel that actually gets posted — to a boarder's home, or a
   family in another town. In-transit parcels sort first, because those are the
   ones a parent rings about.
   --------------------------------------------------------------------------- */
function Shipments() {
  const [rows, setRows] = useState<Shipment[]>([]);
  const [filter, setFilter] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    shipmentsApi.list(filter || undefined).then((r) => setRows(asList(r))).catch(() => setRows([]));
  }, [filter]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setMsg(null);
    try { await fn(); load(); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Action failed.'); }
  };

  const dispatch = (s: Shipment) => {
    const partner = window.prompt('Who is carrying it? (DTDC, India Post, school van…)');
    if (!partner) return;
    const tracking = window.prompt('Tracking / docket number (leave blank if none)') || null;
    act(() => shipmentsApi.dispatch(s.id, { partner, trackingId: tracking, trackingUrl: null }));
  };

  const delivered = (s: Shipment) => {
    const who = window.prompt('Who received it? (name — helps if it is later disputed)') || null;
    act(() => shipmentsApi.delivered(s.id, { receivedBy: who, notes: null }));
  };

  const returned = (s: Shipment) => {
    const reason = window.prompt('Why did it come back? (nobody home, wrong address, refused…)');
    if (!reason) return;
    act(() => shipmentsApi.returned(s.id, reason));
  };

  const colour = (st: string) =>
    st === 'Delivered' ? '#1a7f37' : st === 'Returned' ? '#c1272d'
      : st === 'Dispatched' ? '#b26a00' : 'var(--ink-3)';

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 12 }}>
        Parcels posted to families. Most items are collected at school — create a delivery only when
        something is actually being sent.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All</option>
          <option value="ReadyToDispatch">Ready to dispatch</option>
          <option value="Dispatched">In transit</option>
          <option value="Delivered">Delivered</option>
          <option value="Returned">Returned</option>
        </select>
        <NewShipment onDone={load} />
      </div>

      {msg && <div className="card" style={{ padding: 10, marginBottom: 10, fontSize: 13, color: '#c1272d' }}>{msg}</div>}

      {rows.length === 0
        ? <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>No deliveries.</div>
        : rows.map((s) => (
          <div key={s.id} className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <span style={{ fontWeight: 600 }}>{s.studentName}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {s.subject}</span>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  {s.addressLine}{s.city ? `, ${s.city}` : ''}{s.pincode ? ` — ${s.pincode}` : ''}
                </div>
              </div>
              <span className="pill" style={{ fontSize: 11, color: colour(s.status) }}>{s.status}</span>
            </div>

            {(s.courierPartner || s.trackingId) && (
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 6 }}>
                {s.courierPartner}{s.trackingId ? ` · ${s.trackingId}` : ''}
                {s.dispatchedAt && ` · sent ${formatDate(s.dispatchedAt)}`}
              </div>
            )}
            {s.receivedBy && <div style={{ fontSize: 12, color: '#1a7f37', marginTop: 4 }}>Received by {s.receivedBy}</div>}
            {s.returnReason && <div style={{ fontSize: 12, color: '#c1272d', marginTop: 4 }}>Returned — {s.returnReason}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {s.status === 'ReadyToDispatch' && <button className="btn btn-accent" onClick={() => dispatch(s)}>Dispatch</button>}
              {s.status === 'Dispatched' && <button className="btn btn-accent" onClick={() => delivered(s)}>Mark delivered</button>}
              {s.status === 'Dispatched' && <button className="btn" onClick={() => returned(s)}>Came back</button>}
              {s.status === 'ReadyToDispatch' &&
                <button className="btn" onClick={() => { const r = window.prompt('Cancel — why?'); if (r !== null) act(() => shipmentsApi.cancel(s.id, r || null)); }}>Cancel</button>}
            </div>
          </div>
        ))}
    </div>
  );
}

function NewShipment({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: 'Allocation', subjectId: '', addressLine: '', city: '', pincode: '', phone: '' });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  const create = async () => {
    setErr(null);
    try {
      await shipmentsApi.create({
        subject: f.subject, subjectId: f.subjectId.trim(), addressLine: f.addressLine.trim(),
        city: f.city.trim() || null, pincode: f.pincode.trim() || null, contactPhone: f.phone.trim() || null,
      });
      setOpen(false); setF({ ...f, subjectId: '', addressLine: '', city: '', pincode: '', phone: '' });
      onDone();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not create the delivery.'); }
  };

  if (!open) return <button className="btn btn-accent" onClick={() => setOpen(true)}>+ New delivery</button>;

  return (
    <div className="card" style={{ padding: 14, width: '100%', marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <select className="input" value={f.subject} onChange={set('subject')} style={{ width: 'auto' }}>
          <option value="Allocation">Allocation</option>
          <option value="Order">Parent order</option>
        </select>
        <input className="input mono" placeholder="Allocation / order id" value={f.subjectId} onChange={set('subjectId')} style={{ flex: '2 1 260px', fontSize: 12 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <input className="input" placeholder="Address" value={f.addressLine} onChange={set('addressLine')} style={{ flex: '3 1 240px' }} />
        <input className="input" placeholder="City" value={f.city} onChange={set('city')} style={{ flex: '1 1 120px' }} />
        <input className="input" placeholder="Pincode" value={f.pincode} onChange={set('pincode')} style={{ width: 110 }} />
        <input className="input" placeholder="Phone" value={f.phone} onChange={set('phone')} style={{ width: 140 }} />
      </div>
      {err && <div style={{ color: '#c1272d', fontSize: 13, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-accent" disabled={!f.subjectId.trim() || !f.addressLine.trim()} onClick={create}>Create</button>
        <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}
